import React, { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Button from "../../../../components/Button";
import LinkButton from "../../../../components/LinkButton";
import CheckBox from "../../../../components/CheckBox";
import { useAuthState } from "../../../../components/AuthProvider";
import { USER_AUTH_KEY } from "../../../../utils/constants";
import Icon from "../../../../components/Icon";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from "react-accessible-accordion";

import styles from "./ColumnSelector.module.scss";

const ColumnSelector = ({
  name,
  columnsPrimary,
  staticFields,
  defaultSelectedFields,
  defaultColumnOrder,
  onApply,
  popoverClasses
}) => {
  const [allSelected, setAllSelected] = useState(false);
  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const { user } = useAuthState();

  const allColumns = columnsPrimary
    .map(column => column.accessor)
    .filter(colName => !staticFields.includes(colName));

  const [selectedColumns, setSelectedColumns] = useState(
    new Set(
      user && user.preferences[name] && user.preferences[name].column
        ? user.preferences[name].column
        : defaultSelectedFields
    )
  );

  const [columnOrder, setColumnOrder] = useState(
    user && user.preferences[name] && user.preferences[name].columnOrder
      ? user.preferences[name].columnOrder
      : defaultColumnOrder
  );

  React.useEffect(() => {
    let hiddenColumns = new Set(allColumns);
    selectedColumns.forEach(column => hiddenColumns.delete(column));
    const colOrder = columnOrder;
    onApply(hiddenColumns, colOrder);
  }, []);

  const selectAllHandler = () => {
    if (allSelected) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(allColumns));
    }
    setAllSelected(!allSelected);
  };

  const selectHandler = event => {
    const clonedSet = new Set(selectedColumns);
    if (clonedSet.has(event.target.value)) {
      clonedSet.delete(event.target.value);
    } else {
      clonedSet.add(event.target.value);
    }
    setSelectedColumns(clonedSet);
  };

  const applyHandler = () => {
    user.preferences[name] = {
      column: [...selectedColumns],
      columnOrder: columnOrder
    };
    window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(user));
    setPopoverVisible(false);
    let hiddenColumns = new Set(allColumns);
    selectedColumns.forEach(column => hiddenColumns.delete(column));
    onApply(hiddenColumns, columnOrder);
  };

  const togglePopover = () => {
    setPopoverVisible(!popoverVisible);
  };

  //DragNDrop Handler
  const onDragEnd = result => {
    if (!result.destination) return;
    if (result.type === "DEFAULT") {
      const tempColumnOrder = Array.from(columnOrder);
      const [movedItem] = tempColumnOrder.splice(result.source.index, 1);
      tempColumnOrder.splice(result.destination.index, 0, movedItem);
      setColumnOrder(tempColumnOrder);
    } else {
      const tempColumnOrder = Array.from(columnOrder);
      const categoryIndex = tempColumnOrder.findIndex(
        category => category.heading === result.type
      );
      const [movedItem] = tempColumnOrder[categoryIndex].fields.splice(
        result.source.index,
        1
      );
      tempColumnOrder[categoryIndex].fields.splice(
        result.destination.index,
        0,
        movedItem
      );
      setColumnOrder(tempColumnOrder);
    }
  };
  // style={{ display: "flex", justifyContent: "flex-end" }}
  return (
    <div className={styles.base}>
      <div>
        <LinkButton icon="setting_gear" onClick={togglePopover} />
        <div
          className={`${styles.columnSelectorDiv} ${
            popoverVisible ? styles.isVisible : ""
          }`}
          style={popoverClasses}
        >
          <div className={styles.buttonsContainer}>
            <h3>Show/Hide Columns</h3>
            <Button
              primary
              onClick={applyHandler}
              className={styles.applyButton}
            >
              Apply
            </Button>
            <CheckBox
              label="Select all"
              onChange={selectAllHandler}
              value="all selected"
              selected={allSelected}
              className={styles.selectAll}
            />
          </div>

          <Accordion allowMultipleExpanded className={styles.accordion}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="primaryTableColumnSelector">
                {provided => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {columnOrder.map((category, index) => (
                      //This div represents one catogory (Example - Name)
                      <Draggable
                        key={category.heading}
                        draggableId={category.heading + "draggable"}
                        index={index}
                      >
                        {provided => (
                          <div
                            key={category.heading}
                            {...provided.draggableProps}
                            ref={provided.innerRef}
                          >
                            <AccordionItem className={styles.accordion__item}>
                              <AccordionItemHeading
                                className={styles.accordion__heading}
                              >
                                <AccordionItemButton
                                  className={styles.accordion__button}
                                >
                                  <div className={styles.category}>
                                    <h4>{category.heading}</h4>
                                    <div
                                      {...provided.dragHandleProps} // These are applied to component that we want to use to drag the complete draggable component
                                      className={styles.splitter}
                                      {...provided.dragHandleProps}
                                    >
                                      <Icon
                                        type="splitter_horizontal"
                                        size="1rem"
                                      ></Icon>
                                    </div>
                                  </div>
                                </AccordionItemButton>
                              </AccordionItemHeading>

                              <Droppable
                                droppableId={category.heading + "droppable"}
                                type={`${category.heading}`}
                              >
                                {provided => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                  >
                                    {category.fields.map(
                                      (columnAccessor, index) =>
                                        columnsPrimary
                                          .filter(
                                            ({ accessor }) =>
                                              accessor === columnAccessor
                                          )
                                          .map(column => (
                                            <Draggable
                                              key={columnAccessor}
                                              draggableId={columnAccessor}
                                              index={index}
                                            >
                                              {provided => (
                                                <AccordionItemPanel
                                                  className={
                                                    styles.accordion__panel
                                                  }
                                                >
                                                  <div
                                                    key={columnAccessor}
                                                    {...provided.draggableProps}
                                                    ref={provided.innerRef}
                                                  >
                                                    <div
                                                      {...provided.dragHandleProps} // These are applied to component that we want to use to drag the complete draggable component
                                                      className={
                                                        styles.splitter
                                                      }
                                                    >
                                                      <Icon
                                                        type="splitter_horizontal"
                                                        size="1rem"
                                                      ></Icon>
                                                    </div>
                                                    <CheckBox
                                                      key={column.accessor}
                                                      className={
                                                        styles.columnBoxes
                                                      }
                                                      label={column.Header}
                                                      value={column.accessor}
                                                      onChange={selectHandler}
                                                      selected={selectedColumns.has(
                                                        column.accessor
                                                      )}
                                                    />
                                                  </div>
                                                </AccordionItemPanel>
                                              )}
                                            </Draggable>
                                          ))
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </AccordionItem>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelector;
